import { useCallback, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { apiUrl } from "../../config";
import { useGetAccessToken } from "../../client";
import { useImportKontakteCsv } from "../../client/hooks/useImportKontakteCsv";
import { type KontakteImportReport } from "../../api";

export function useImportKontakte(aufgabeId: string) {
  const getAccessToken = useGetAccessToken();
  const [validationError, setValidationError] = useState<string | null>(null);
  const importKontakteCsv = useImportKontakteCsv();

  const validateFile = useCallback((file: File | null) => {
    if (!file) {
      setValidationError("no-file-selected");
      return false;
    }
    const isCsv = file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
    if (!isCsv) {
      setValidationError("invalid-file-type");
      return false;
    }
    // 5MB soft limit (server enforces the same)
    if (file.size > 5 * 1024 * 1024) {
      setValidationError("file-too-large");
      return false;
    }
    setValidationError(null);
    return true;
  }, []);

  const templateDownload = useCallback(async () => {
    const token = await getAccessToken();
    const res = await fetch(`${apiUrl}/api/v1/kontakte/import-template`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error("template-download-failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-kontakte-import.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [getAccessToken]);

  const mutation = useMutation<KontakteImportReport, Error, File>(async (file: File) => {
    if (!validateFile(file)) throw new Error(validationError ?? "validation-failed");
    // Delegate import to generated API hook
    return await importKontakteCsv(aufgabeId, { file });
  });

  // Ensure that external consumers can fully reset both the mutation and validation state
  const resetAll = useCallback(() => {
    mutation.reset();
    setValidationError(null);
  }, [mutation]);

  return useMemo(
    () => ({
      templateDownload,
      importCsv: mutation.mutateAsync,
      isUploading: mutation.isLoading,
      report: mutation.data ?? null,
      error: mutation.error ?? null,
      validateFile,
      validationError,
      reset: resetAll,
    }),
    [templateDownload, mutation, validateFile, validationError, resetAll]
  );
}
